import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Chip,
  Avatar,
  Divider,
  LinearProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';

interface Franchisee {
  id: string;
  code: string;
  name: string;
  franchiseeOwner?: string;
  franchiseeEmail?: string;
  franchiseePhone?: string;
  complianceScore?: number;
  royaltyRate?: number;
  growthRate?: number;
  brand?: { name: string };
  region?: { name: string };
  latitude?: number;
  longitude?: number;
}

interface FranchiseDetailDialogProps {
  open: boolean;
  franchisee: Franchisee | null;
  onClose: () => void;
}

const getComplianceStatus = (score: number | null | undefined) => {
  const s = score ?? 0;
  if (s >= 95) return { color: '#388e3c', label: 'Excellent' };
  if (s >= 85) return { color: '#f57c00', label: 'Good' };
  return { color: '#d32f2f', label: 'Critical' };
};

export const FranchiseDetailDialog = ({ open, franchisee, onClose }: FranchiseDetailDialogProps) => {
  if (!franchisee) return null;

  const complianceStatus = getComplianceStatus(franchisee.complianceScore);
  const complianceScore = franchisee.complianceScore ?? 0;
  const royaltyRate = franchisee.royaltyRate ?? 0;

  // Generate map embed URL (using OpenStreetMap/Nominatim)
  // For a production app, you'd want to use Leaflet or Google Maps
  const mapUrl = franchisee.latitude && franchisee.longitude
    ? `https://www.openstreetmap.org/export/embed.html?bbox=${franchisee.longitude - 0.01},${franchisee.latitude - 0.01},${franchisee.longitude + 0.01},${franchisee.latitude + 0.01}&layer=mapnik&marker=${franchisee.latitude},${franchisee.longitude}`
    : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {franchisee.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Code: {franchisee.code}
          </Typography>
        </Box>
        <Button
          onClick={onClose}
          sx={{ minWidth: 'auto', p: 1 }}
        >
          <CloseIcon />
        </Button>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {/* Owner & Contact Info */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: '#666' }}>
            Contact Information
          </Typography>
          {franchisee.franchiseeOwner && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Avatar sx={{ width: 32, height: 32, bgcolor: '#1976d2', fontSize: '0.9rem' }}>
                {franchisee.franchiseeOwner.charAt(0).toUpperCase()}
              </Avatar>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {franchisee.franchiseeOwner}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Owner
                </Typography>
              </Box>
            </Box>
          )}

          {franchisee.franchiseeEmail && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <EmailIcon sx={{ fontSize: 20, color: '#1976d2' }} />
              <Typography variant="body2" sx={{ color: '#1976d2', wordBreak: 'break-all' }}>
                {franchisee.franchiseeEmail}
              </Typography>
            </Box>
          )}

          {franchisee.franchiseePhone && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PhoneIcon sx={{ fontSize: 20, color: '#2E7D32' }} />
              <Typography variant="body2">
                {franchisee.franchiseePhone}
              </Typography>
            </Box>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Compliance & Performance */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: '#666' }}>
            Compliance & Performance
          </Typography>

          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Compliance Score
              </Typography>
              <Chip
                label={`${complianceScore.toFixed(0)}%`}
                size="small"
                sx={{
                  bgcolor: complianceStatus.color,
                  color: 'white',
                  fontWeight: 600,
                }}
              />
            </Box>
            <LinearProgress
              variant="determinate"
              value={complianceScore}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: '#f0f0f0',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 4,
                  backgroundColor: complianceStatus.color,
                },
              }}
            />
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Status
            </Typography>
            <Chip
              label={complianceStatus.label}
              size="small"
              sx={{
                bgcolor: complianceStatus.color + '20',
                color: complianceStatus.color,
                fontWeight: 600,
              }}
            />
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Financial Summary */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: '#666' }}>
            Financial Summary
          </Typography>

          <Grid container spacing={1.5}>
            <Grid item xs={6}>
              <Card sx={{ bgcolor: '#f5f5f5', textAlign: 'center' }}>
                <CardContent sx={{ py: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 0.5 }}>
                    <MoneyIcon sx={{ color: '#2E7D32', fontSize: 20 }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#2E7D32' }}>
                    {(royaltyRate * 100).toFixed(1)}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Royalty Rate
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6}>
              <Card sx={{ bgcolor: '#f5f5f5', textAlign: 'center' }}>
                <CardContent sx={{ py: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 0.5 }}>
                    <TrendingUpIcon sx={{ color: franchisee.growthRate && franchisee.growthRate > 0 ? '#2E7D32' : '#D32F2F', fontSize: 20 }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: franchisee.growthRate && franchisee.growthRate > 0 ? '#2E7D32' : '#D32F2F' }}>
                    {franchisee.growthRate !== undefined ? franchisee.growthRate.toFixed(1) : 'N/A'}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Growth Rate
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Organization */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: '#666' }}>
            Organization
          </Typography>

          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            {franchisee.brand && (
              <Chip
                label={franchisee.brand.name}
                variant="outlined"
                sx={{ fontWeight: 500 }}
              />
            )}
            {franchisee.region && (
              <Chip
                label={franchisee.region.name}
                variant="outlined"
                sx={{ fontWeight: 500 }}
              />
            )}
          </Box>
        </Box>

        {/* Map */}
        {mapUrl && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: '#666' }}>
                Location
              </Typography>
              <Box
                component="iframe"
                src={mapUrl}
                sx={{
                  width: '100%',
                  height: 250,
                  border: '1px solid #ddd',
                  borderRadius: 1,
                  mb: 2,
                }}
              />
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
